import type { ComponentChild, FunctionComponent, TargetedEvent } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { createSelectRepeater, SelectContextProvider } from 'Components/smallComponents/select';
import { type InputTemplateFunction, RepeaterFunctionInvoker, type TemplateFunction } from 'Components/types';
import { vehicleDiscardingRequested, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import { assignLocation } from 'Core/actions/redirect';
import {
  discardField,
  discardManyFields,
  replaceManyValues,
  replaceValue,
  toggle,
} from 'Core/actions/request';
import useAutonomousSearchHook, { type NormResponse } from 'Core/hooks/autonomousSearch';
import { useCollapsible } from 'Core/hooks/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchFieldsSelector,
} from 'Core/selectors/fitmentSearch/index';
import { createFilteredFacetsSelector } from 'Core/selectors/search';
import { resultsLoadingSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { type SelectionEntry, Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import { getUriFromRequest } from 'Modules/converter/index';
import { cloneSafe } from 'Utils/components';
import isTruthy from 'Utils/isTruthy';
import safeTemplateCall from 'Utils/safeTemplateCall';
import t from 'Utils/translate';
import { getItem, removeItem, setItem } from 'Utils/userStorage';

type StoredSelection = Array<SelectionEntry & { value: string }>;

/**
 * Predicate for the `localStorageKey` slot used by the request panel.
 * Validates each entry has the three string fields the rest of the
 * panel reads via `getSelectionObject`. Extra fields are tolerated.
 */
function isStoredSelection(value: unknown): value is StoredSelection {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        typeof (entry as { field?: unknown }).field === 'string' &&
        typeof (entry as { term?: unknown }).term === 'string' &&
        typeof (entry as { value?: unknown }).value === 'string',
    )
  );
}

type Params = {
  name: string;
  collapsed: boolean;
  disableClear: boolean;
  inputs: RepeaterFunctionInvoker<InputsRepeater>;
  hasFullSelection: boolean;
  hasRequiredSelection: boolean;
  hasSelection: boolean;
  autoOpenDropdowns?: 'all' | 'next';
  selection: Record<string, string>;
  selects: ReturnType<typeof createSelectRepeater>;
  clear: () => void;
  submit: () => void;
  toggleCollapsed: () => void;
};

type InputsRepeater = {
  facetInput: (template: InputTemplateFunction) => ComponentChild;
};

type Props = {
  name: string;
  template: TemplateFunction<Params>;
  initCollapsed?: boolean;
  inputFields?: string[];
  isAutoRedirectDisabled?: true;
  discardVehicleOnSubmit?: false;
  onSubmit?: () => void;
  requiredFields?: string[];
  redirectUrl?: string;
  requestExtra?: RequestExtra;
  selectFields?: string[];
  shouldBeIsolated?: () => void;
  mayDiscardIsolatedSelection?: boolean;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
  autoOpenDropdowns?: 'all' | 'next';
  localStorageKey?: string;
};

const RequestPanel: FunctionComponent<Props> = ({
  name,
  template,
  isAutoRedirectDisabled,
  initCollapsed = false,
  inputFields,
  onSubmit,
  discardVehicleOnSubmit,
  requiredFields,
  redirectUrl,
  requestExtra,
  selectFields = [],
  shouldBeIsolated,
  mayDiscardIsolatedSelection,
  useNativeDropdown,
  useSearchableDropdown,
  autoOpenDropdowns,
  localStorageKey,
}) => {
  const dispatch = useDispatch();
  const rootRef = useRef<HTMLElement>(null);
  const unselectedRef = useRef<(() => void) | null>(null);

  const { isCollapsed: collapsed, toggle: toggleCollapsed } = useCollapsible(rootRef, null, initCollapsed);

  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const fields = useMemo(() => [...selectFields, ...(inputFields || [])], [inputFields, selectFields]);

  const useAutonomousSearch = !requestConfig.hasSearchResults || !!shouldBeIsolated?.();

  const [storedSelectionObj, setStoredSelectionObj] = useState<Record<string, string>>(() => {
    if (localStorageKey) {
      const storedSelection = getItem(localStorageKey, isStoredSelection);
      if (storedSelection?.length) {
        return getSelectionObject(storedSelection);
      }
    }
    return {};
  });

  const {
    isLoading: isAutonomousLoading,
    isResponsePending: isAutonomousResponsePending,
    response: autonomousResponse,
    clear: autonomousClear,
    onChange: autonomousOnChange,
  } = useAutonomousSearchHook(fields, requestExtra, storedSelectionObj);

  const isSearchLoading = useSelector(resultsLoadingSelector);
  const searchResponse = useSelector(useMemo(() => createFilteredFacetsSelector(fields), [fields]));

  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const fitmentBaseFields = useSelector(fitmentSearchBaseFieldsSelector);

  const normSearchResponse = useMemo<NormResponse>(
    () =>
      searchResponse.map((facet) => ({
        field: facet.field,
        title: facet.name,
        entries: facet.values?.map((value) => ({ ...value, selected: value.isSelected })),
      })),
    [searchResponse],
  );

  const isLoading = useAutonomousSearch ? isAutonomousLoading : isSearchLoading;
  const response = useAutonomousSearch ? autonomousResponse : normSearchResponse;
  const hasFacetResponse = response.length > 0;
  const isAutoOpenPending =
    !!autoOpenDropdowns && (isLoading || (useAutonomousSearch && isAutonomousResponsePending));

  const clear = () => {
    if (!useAutonomousSearch || (mayDiscardIsolatedSelection && requestConfig.hasSearchResults)) {
      dispatch(discardManyFields(fields));
    }

    if (useAutonomousSearch) {
      autonomousClear();
    }

    setInputValues({});

    if (localStorageKey) {
      removeItem(localStorageKey);
    }
  };

  const onValueSelected = (field: string, value: string, isSingleValue: boolean) => {
    if (autoOpenDropdowns) {
      unselectedRef.current = null;
    }

    if (useAutonomousSearch) {
      autonomousOnChange(field)(value);
      return;
    }

    if (isSingleValue) {
      dispatch(toggle({ field, term: value }, { isSingleValue: true, mayDiscardValue: true }));
      return;
    }

    dispatch(replaceValue({ field, term: value }, { mayDiscardValue: true }));
  };

  const selection = Object.fromEntries(
    fields.map((field) => [
      field,
      response.find((facet) => facet.field === field)?.entries.find((v) => v.selected)?.value || '',
    ]),
  );

  const nonEmptyResponseFacets = response.filter((f) => !!f.entries.length);

  const hasSelection = Object.keys(selection).some((field) => !!selection[field] || !!inputValues[field]);
  const hasRequiredSelection =
    hasSelection &&
    !!requiredFields?.every(
      (field) =>
        !nonEmptyResponseFacets.find((f) => f.field === field) ||
        !!nonEmptyResponseFacets.find((f) => f.field === field && f.entries.some((v) => v.selected)),
    );
  const hasFullSelection =
    nonEmptyResponseFacets.length > 0 &&
    nonEmptyResponseFacets.every((f) => f.entries.some((v) => v.selected));

  const disableClear = Object.keys(selection).filter((field) => !!selection[field]).length === 0;

  const submit = useCallback(() => {
    if (autoOpenDropdowns) {
      if (isAutoOpenPending) {
        return;
      }

      const open = unselectedRef.current;
      if (open) {
        open();
        return;
      }
    }

    const redirectPathname = redirectUrl ?? uiConfig.searchPageUrl;
    const requestSelection = fields
      .map((field) => {
        const selectedEntry = response.find((f) => f.field === field)?.entries.find((v) => v.selected);
        return selectedEntry && { field, term: selectedEntry.term };
      })
      .filter((v): v is SelectionEntry => Boolean(v));

    if (useAutonomousSearch && window.location.pathname !== redirectPathname) {
      const { href: redirectUrl } = getUriFromRequest(
        { selection: requestSelection },
        { pathname: redirectPathname },
      );

      if (discardVehicleOnSubmit) {
        dispatch(vehicleDiscardingRequested({ mayDiscardValue: false }));
      }

      dispatch(assignLocation(redirectUrl));
    } else {
      // case of using RequestPanel as a VehicleWidget in combination with regular facets
      const requestFitmentSelection = requestSelection.filter((facet) => fitmentFields.includes(facet.field));
      const requestFitmentSelectionFields = requestFitmentSelection.map((facet) => facet.field);

      if (fitmentBaseFields.every((field) => requestFitmentSelectionFields.includes(field))) {
        const vehicle = new Vehicle(
          requestFitmentSelection.reduce((result, facet) => ({ ...result, [facet.field]: facet.term }), {}),
          fitmentFields,
        );

        dispatch(
          vehicleSelected(vehicle, {
            doNotRedirectOnVehicleSelect: isAutoRedirectDisabled,
            isolatedKey: null,
          }),
        );

        fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
      }

      dispatch(
        replaceManyValues(requestSelection, {
          pathname: redirectPathname,
        }),
      );
    }

    onSubmit?.();

    if (!collapsed) {
      toggleCollapsed();
    }
  }, [
    collapsed,
    dispatch,
    fields,
    fitmentBaseFields,
    fitmentFields,
    isAutoRedirectDisabled,
    discardVehicleOnSubmit,
    onSubmit,
    redirectUrl,
    response,
    toggleCollapsed,
    useAutonomousSearch,
    autoOpenDropdowns,
    isAutoOpenPending,
  ]);

  useEffect(
    function autoSubmit() {
      if (isAutoRedirectDisabled || !useAutonomousSearch) {
        return;
      }

      if (hasFullSelection) {
        submit();
      }
    },
    [isAutoRedirectDisabled, hasFullSelection, submit, useAutonomousSearch],
  );

  useEffect(() => {
    const nextInputValues = response
      .filter(({ field, entries }) => inputFields?.includes(field) && !!entries.length)
      .reduce<Record<string, string>>((map, { field, entries }) => {
        map[field] = entries.find((entry) => entry.selected)?.value?.replace(' TO *', '') ?? '';
        return map;
      }, {});

    setInputValues(nextInputValues);
  }, [response, inputFields, isLoading]);

  const currentSelection = useMemo(() => {
    return getStoredSelection(fields, response);
  }, [fields, response]);

  useEffect(() => {
    if (!localStorageKey || isLoading) {
      return;
    }

    const prevSelection = getItem(localStorageKey, isStoredSelection) ?? [];

    if (JSON.stringify(currentSelection) !== JSON.stringify(prevSelection)) {
      if (currentSelection.length > 0 && hasRequiredSelection) {
        setItem(localStorageKey, currentSelection);
        setStoredSelectionObj(getSelectionObject(currentSelection));
      } else {
        removeItem(localStorageKey);
        setStoredSelectionObj({});
      }
    }
  }, [currentSelection, localStorageKey, isLoading, hasRequiredSelection]);

  if (!isLoading && !response.length) {
    return null;
  }

  const stubSelects = createSelectRepeater(
    selectFields.map((field) => {
      const title = t(`facet_title_${field.replaceAll(' ', '_')}`) || field;

      return {
        field,
        entries: [{ value: title, term: '', selected: false }],
        hideNullOption: true,
        title,
        key: field,
        isSearchable: useSearchableDropdown,
        useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
        onChange: () => {
          /* stub */
        },
      };
    }),
  );

  const unselectedField = autoOpenDropdowns
    ? selectFields.find((field) => {
        const fieldData = response.find(({ field: responseField }) => responseField === field);
        return !!fieldData?.entries?.length && !fieldData.entries.some((entry) => entry.selected);
      })
    : undefined;

  const actualSelects = createSelectRepeater(
    selectFields.map((field, index) => {
      const fieldData = response.find(({ field: f }) => f === field);
      return {
        field,
        title: fieldData?.title || field,
        useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
        isSearchable: useSearchableDropdown,
        entries: fieldData?.entries || [],
        onChange: (value: string) => onValueSelected(field, value, true),
        key: field,
        disabled: !fieldData?.entries?.length,
        initialOpen:
          autoOpenDropdowns && field === unselectedField && (autoOpenDropdowns === 'all' || index > 0),
        openRef: autoOpenDropdowns && field === unselectedField ? unselectedRef : undefined,
      };
    }),
  );

  const stubInputs: RepeaterFunctionInvoker<InputsRepeater> = (inputFields ?? []).map(
    (field) => (template: TemplateFunction<InputsRepeater>) => {
      const facetInput = (templ: InputTemplateFunction) => {
        const templProps = templ()?.props;

        const props = {
          disabled: true,
          placeholder: templProps?.placeholder ?? `Enter ${field}`,
          onChange: () => {
            /* stub */
          },
          onBlur: () => {
            /* stub */
          },
        };

        return <input {...props} />;
      };

      return safeTemplateCall(template, { facetInput }, 'RequestPanelStubInput');
    },
  );

  const actualInputs: RepeaterFunctionInvoker<InputsRepeater> = response
    .filter((facet) => inputFields?.includes(facet.field))
    .map(({ field, title }) => (template: TemplateFunction<InputsRepeater>) => {
      const facetInput = (templ: InputTemplateFunction) => {
        const templProps = templ()?.props;

        const props = {
          autoComplete: 'off',
          disabled: templProps?.disabled,
          placeholder: templProps?.placeholder ?? `Enter ${title}`,
          type: 'number',
          value: inputValues[field] ?? '',
          onChange: (e: TargetedEvent<HTMLInputElement, Event>) => {
            const { value } = e.currentTarget;
            setInputValues({ ...inputValues, [field]: value });
          },
          onBlur: (e: TargetedEvent<HTMLInputElement, Event>) => {
            const { value } = e.currentTarget;
            if (value) {
              onValueSelected(field, `${value} TO *`, false);
            } else if (!useAutonomousSearch) {
              dispatch(discardField(field, { mayDiscardValue: true }));
            }
          },
        };

        return <input {...props} key={`input-${field}`} />;
      };

      return safeTemplateCall(template, { facetInput }, 'RequestPanelActualInput');
    });

  const component = safeTemplateCall(
    template,
    {
      name,
      selects: hasFacetResponse ? actualSelects : stubSelects,
      inputs: hasFacetResponse ? actualInputs : stubInputs,
      collapsed,
      clear,
      disableClear,
      hasSelection,
      hasRequiredSelection,
      hasFullSelection,
      autoOpenDropdowns,
      selection,
      submit,
      toggleCollapsed,
    },
    'RequestPanel',
  );
  return <SelectContextProvider>{cloneSafe(component, rootRef)}</SelectContextProvider>;
};

export default RequestPanel;

function getStoredSelection(fields: string[], response: NormResponse): StoredSelection {
  return fields
    .map((field) => {
      const selectedEntry = response.find((f) => f.field === field)?.entries.find((v) => v.selected);
      if (selectedEntry) {
        return { field, term: selectedEntry.term, value: selectedEntry.value };
      }
      return null;
    })
    .filter(isTruthy);
}

function getSelectionObject(storedSelection: StoredSelection): Record<string, string> {
  return Object.fromEntries(
    storedSelection.filter((item) => item.field && item.term).map((item) => [item.field, item.term]),
  );
}
